import React, { useState, useEffect, useRef } from 'react'
import { api, formatCurrency, formatDate } from '../../utils/api'
import { DEAL_STAGES, getSearchUrls, getStageName } from '../../utils/dealConstants'

function getValidationBadge(log) {
  if (!log) return null
  if (log.includes('⚠ MODEL VALIDATION WARNING') || log.includes('⚠ LOW IDENTITY CONFIDENCE')) {
    return { className: 'validation-badge warning', label: '⚠ Validation Warning' }
  }
  if (log.includes('--- BLOCKED ---')) {
    return { className: 'validation-badge blocked', label: 'Blocked (legacy)' }
  }
  return { className: 'validation-badge passed', label: '✓ Validated' }
}

// Parse Claude pricing log into structured sections
function parsePricingLogSections(log) {
  if (!log) return []

  const sectionDefs = [
    { marker: /^\[\d{4}-\d{2}-\d{2}/, title: 'CLAUDE PRICING LOG' },
    { marker: /^Status:\s*CONFIRMED|^Visible Text:|^---\s*MODEL VALIDATION\s*---/m, title: 'MODEL VALIDATION' },
    { marker: /^IC:\s|^---\s*CONFIDENCE SCORES\s*---/m, title: 'CONFIDENCE SCORES' },
    { marker: /^Score:\s*\d+\s*\(|^---\s*DATASET CONFIDENCE SCORE\s*---/m, title: 'DATASET CONFIDENCE SCORE' },
    { marker: /^Total filtered:|^---\s*CONDITION SEGMENTATION\s*---/m, title: 'CONDITION SEGMENTATION' },
    { marker: /^Method:\s*(AI|Rule)|^---\s*SEGMENT CLASSIFICATION\s*---/m, title: 'SEGMENT CLASSIFICATION' },
    { marker: /^Text SKUs:|^---\s*RECONCILIATION\s*---/m, title: 'RECONCILIATION' },
    { marker: /^---\s*SMALL SAMPLE|^Cleaned sold|^---\s*PRICE BUCKETING ANALYSIS\s*---/m, title: 'PRICE BUCKETING ANALYSIS' },
    { marker: /^Intake condition:|^Baseline price|^---\s*CONDITION ADJUSTMENT\s*---/m, title: 'CONDITION ADJUSTMENT' },
    { marker: /^Sold Prices \(shipped\):|^Active BIN:|^---\s*EBAY UK SOLD/m, title: 'EBAY UK SOLD' },
    { marker: /^Used\/eBay:|^---\s*GOOGLE SHOPPING/m, title: 'GOOGLE SHOPPING' },
    { marker: /^Amazon:|^---\s*AMAZON UK/m, title: 'AMAZON UK' },
    { marker: /^Sold:.*Active:|^Collection-only \(local|^---\s*REVERB UK/m, title: 'REVERB UK' },
    { marker: /^Intake brand:|^Model compare:|^---\s*MODEL VALIDATION GATE\s*---/m, title: 'MODEL VALIDATION GATE' },
    { marker: /^R2 lookup:|^Found \d+ images|^\[AZURE-OCR\]|^---\s*IMAGE DEBUG\s*---/m, title: 'IMAGE DEBUG' },
  ]

  const lines = log.split('\n')
  const sections = []
  let currentTitle = 'CLAUDE PRICING LOG'
  let currentLines = []

  for (const line of lines) {
    // Skip existing --- SECTION --- markers (we'll add our own headers)
    if (/^---\s*[A-Z].*---\s*$/.test(line.trim())) {
      // Check if this line itself triggers a new section
      let matched = false
      for (const def of sectionDefs) {
        if (def.title !== currentTitle && def.marker.test(line)) {
          if (currentLines.length > 0) {
            sections.push({ title: currentTitle, content: currentLines.join('\n').trim() })
          }
          currentTitle = def.title
          currentLines = []
          matched = true
          break
        }
      }
      if (!matched) continue // skip standalone --- markers
      continue
    }

    // Skip trailing bare "---" lines
    if (line.trim() === '---') continue

    let matched = false
    for (const def of sectionDefs) {
      if (def.title !== currentTitle && def.marker.test(line)) {
        if (currentLines.length > 0) {
          sections.push({ title: currentTitle, content: currentLines.join('\n').trim() })
        }
        currentTitle = def.title
        currentLines = [line]
        matched = true
        break
      }
    }
    if (!matched) {
      currentLines.push(line)
    }
  }
  if (currentLines.length > 0) {
    sections.push({ title: currentTitle, content: currentLines.join('\n').trim() })
  }

  // Merge duplicate titles
  const merged = []
  const seen = new Set()
  for (const sec of sections) {
    if (seen.has(sec.title)) {
      const existing = merged.find(m => m.title === sec.title)
      if (existing) existing.content += '\n' + sec.content
    } else {
      seen.add(sec.title)
      merged.push({ ...sec })
    }
  }

  return merged.filter(s => s.content)
}

function QuoteRequested() {
  const [deals, setDeals] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')

  // Editing state
  const [editingCell, setEditingCell] = useState(null) // { dealId, field }
  const [editValue, setEditValue] = useState('')
  const [savingField, setSavingField] = useState(null)

  // Item field editing state
  const [editingPrice, setEditingPrice] = useState(null) // { dealId, index, field }
  const [priceValue, setPriceValue] = useState('')
  const [savingPrice, setSavingPrice] = useState(null)

  // Expanded item state
  const [selectedItem, setSelectedItem] = useState(null) // { dealId, index }

  // Customer dropdown state
  const [expandedDeal, setExpandedDeal] = useState(null)
  const [customerDeals, setCustomerDeals] = useState({})
  const [loadingCustomerDeals, setLoadingCustomerDeals] = useState({})

  // Action states
  const [updatingDeal, setUpdatingDeal] = useState(null)
  const [rejectingDeal, setRejectingDeal] = useState(null)
  const [sendingConsignment, setSendingConsignment] = useState(null)
  const [markingDuplicate, setMarkingDuplicate] = useState(null)

  // More info modal
  const [moreInfoDeal, setMoreInfoDeal] = useState(null)
  const [moreInfoOptions, setMoreInfoOptions] = useState({ photosInfo: false, other: false })
  const [otherText, setOtherText] = useState('')
  const [sendingMoreInfo, setSendingMoreInfo] = useState(false)

  // Deal images (R2)
  const [dealImages, setDealImages] = useState({})
  const [loadingDealImages, setLoadingDealImages] = useState(null)
  const [expandedDealImages, setExpandedDealImages] = useState(null)

  // Lightbox state
  const [lightbox, setLightbox] = useState({ open: false, images: [], currentIndex: 0 })

  // Re-price state
  const [repricingItem, setRepricingItem] = useState(null) // { dealId, index }

  // Other deal details popup
  const [selectedOtherDeal, setSelectedOtherDeal] = useState(null)
  const [updatingOtherDealStage, setUpdatingOtherDealStage] = useState(false)

  // Pricing log editing
  const [editingPricingLog, setEditingPricingLog] = useState(null) // dealId
  const [pricingLogValue, setPricingLogValue] = useState('')
  const [savingPricingLog, setSavingPricingLog] = useState(false)
  const [markingOtherDuplicate, setMarkingOtherDuplicate] = useState(false)

  // Badly priced feedback
  const [badlyPricedDeal, setBadlyPricedDeal] = useState(null)
  const [badlyPricedItems, setBadlyPricedItems] = useState({}) // { [itemIndex]: issueType }
  const [badlyPricedComments, setBadlyPricedComments] = useState({}) // { [itemIndex]: comment string }
  const [submittingBadlyPriced, setSubmittingBadlyPriced] = useState(false)
  const [badlyPricedSuccess, setBadlyPricedSuccess] = useState(null) // dealId shown as "Reported"

  // Open search URL in popup window
  const openWindow = (url) => window.open(url, '_blank', 'width=1200,height=800')

  function openLightbox(images, startIndex = 0) {
    setLightbox({ open: true, images, currentIndex: startIndex })
  }

  function closeLightbox() {
    setLightbox({ open: false, images: [], currentIndex: 0 })
  }

  function nextImage() {
    setLightbox(prev => ({
      ...prev,
      currentIndex: (prev.currentIndex + 1) % prev.images.length
    }))
  }

  function prevImage() {
    setLightbox(prev => ({
      ...prev,
      currentIndex: (prev.currentIndex - 1 + prev.images.length) % prev.images.length
    }))
  }

  // Handle keyboard navigation in lightbox
  useEffect(() => {
    if (!lightbox.open) return

    function handleKeyDown(e) {
      if (e.key === 'Escape') closeLightbox()
      if (e.key === 'ArrowRight') nextImage()
      if (e.key === 'ArrowLeft') prevImage()
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [lightbox.open])

  useEffect(() => {
    loadDeals()
  }, [])

  async function loadDeals() {
    setLoading(true)
    try {
      const response = await api.get('/api/deals')
      setDeals(response.deals || [])
    } catch (error) {
      console.error('Failed to load deals:', error)
    } finally {
      setLoading(false)
    }
  }

  // Parse items from parsed_items string
  function parseItems(itemsString) {
    if (!itemsString) return []
    return itemsString.split('\n').filter(line => line.trim())
  }

  // Extract score from claude_pricing_log (fallback for single-item deals)
  function getScoreFromLog(log) {
    if (!log) return null
    const match = log.match(/Score:\s*(\d+)/)
    return match ? parseInt(match[1]) : null
  }

  // Parse a single item line into components
  // Handles both old format: "1. Name - Details - Qty:1 - SP:£709 - BP:£351"
  // and new format: "Name, condition, qty 6"
  function parseItemLine(line) {
    const withoutNumber = line.replace(/^\d+\.\s*/, '')
    const spMatch = withoutNumber.match(/\bSP[-:.]?\s*£?([\d.]+)/i)
    const bpMatch = withoutNumber.match(/\bBP[-:.]?\s*£?([\d.]+)/i)
    const qtyMatch = withoutNumber.match(/\bQty[:\s]\s*(\d+)/i)
    const scoreMatch = withoutNumber.match(/\bSc:(\d+)/i)

    let description = withoutNumber
      .replace(/\s*-\s*Qty[:\s]\s*\d+/i, '')
      .replace(/,\s*qty\s+\d+/i, '')
      .replace(/\s*-?\s*\bSP[-:.]?\s*£?[\d.]+/i, '')
      .replace(/\s*-?\s*\bBP[-:.]?\s*£?[\d.]+/i, '')
      .replace(/\s*-\s*Sc:\d+/i, '')
      .replace(/\s*\(PENDING\)/i, '')
      .trim()

    // Extract condition from " - condition" format (new) or ", condition" format (legacy)
    const knownConditions = ['excellent', 'good', 'fair', 'poor', 'new', 'mint', 'not working']
    let condition = null
    const lastDash = description.lastIndexOf(' - ')
    if (lastDash > 0) {
      const afterDash = description.substring(lastDash + 3).trim().toLowerCase()
      if (knownConditions.includes(afterDash)) {
        condition = description.substring(lastDash + 3).trim()
        description = description.substring(0, lastDash).trim()
      }
    }
    if (!condition) {
      const descParts = description.split(',').map(p => p.trim())
      if (descParts.length >= 2) {
        const lastPart = descParts[descParts.length - 1].toLowerCase()
        if (knownConditions.includes(lastPart)) {
          condition = descParts.pop().trim()
          description = descParts.join(', ').trim()
        }
      }
    }

    return {
      description,
      condition,
      qty: qtyMatch ? qtyMatch[1] : '1',
      sp: spMatch ? spMatch[1] : '0',
      bp: bpMatch ? bpMatch[1] : '0',
      score: scoreMatch ? parseInt(scoreMatch[1]) : null
    }
  }

  // Reconstruct item line from components
  function reconstructItemLine(index, description, qty, sp, bp, score = null, condition = null) {
    const condStr = condition ? ` - ${condition}` : ''
    const scoreStr = score ? ` - Sc:${score}` : ''
    return `${index + 1}. ${description} - Qty:${qty}${condStr} - SP:£${sp} - BP:£${bp}${scoreStr}`
  }

  // Update deal property
  async function updateDealProperty(dealId, field, value) {
    setSavingField(`${dealId}-${field}`)
    try {
      await api.patch(`/api/deals/${dealId}`, { [field]: value })
      setDeals(deals.map(d => d.id === dealId ? { ...d, [field]: value } : d))
      setEditingCell(null)
    } catch (error) {
      alert(`Error updating: ${error.message}`)
    } finally {
      setSavingField(null)
    }
  }

  // Update item field (SP, BP, Qty, or description)
  async function updateItemField(dealId, itemIndex, field, newValue, currentItems) {
    const items = [...currentItems]
    const parsed = parseItemLine(items[itemIndex])

    if (field === 'sp') parsed.sp = newValue
    else if (field === 'bp') parsed.bp = newValue
    else if (field === 'qty') parsed.qty = newValue
    else if (field === 'description') parsed.description = newValue
    else if (field === 'condition') parsed.condition = newValue || null

    items[itemIndex] = reconstructItemLine(itemIndex, parsed.description, parsed.qty, parsed.sp, parsed.bp, parsed.score, parsed.condition)
    const newItemsString = items.join('\n')
    await updateDealProperty(dealId, 'parsed_items', newItemsString)
  }

  // Delete item and renumber
  async function deleteParsedItem(dealId, index, currentItems) {
    const items = currentItems.filter((_, i) => i !== index)
    const renumberedItems = items.map((item, newIndex) => {
      const parsed = parseItemLine(item)
      return reconstructItemLine(newIndex, parsed.description, parsed.qty, parsed.sp, parsed.bp, parsed.score, parsed.condition)
    })
    const newItemsString = renumberedItems.join('\n')
    await updateDealProperty(dealId, 'parsed_items', newItemsString)
  }

  // Add new item
  async function addParsedItem(dealId, currentItems) {
    const newIndex = currentItems.length
    const newItem = reconstructItemLine(newIndex, 'New Item', '1', '0', '0')
    const newItemsString = currentItems.length > 0 ? [...currentItems, newItem].join('\n') : newItem
    await updateDealProperty(dealId, 'parsed_items', newItemsString)
    setTimeout(() => {
      setEditingPrice({ dealId, index: newIndex, field: 'description' })
      setPriceValue('New Item')
    }, 100)
  }

  // Save field edit
  async function saveFieldEdit(dealId, index, field, items) {
    if (!editingPrice) return
    setSavingPrice(`${dealId}-${index}-${field}`)
    try {
      await updateItemField(dealId, index, field, priceValue, items)
    } catch (err) {
      alert(`Error: ${err.message}`)
    } finally {
      setSavingPrice(null)
      setEditingPrice(null)
    }
  }

  // Save pricing log edit
  async function savePricingLog(dealId) {
    setSavingPricingLog(true)
    try {
      await api.patch(`/api/deals/${dealId}`, { claude_pricing_log: pricingLogValue })
      setDeals(deals.map(d => d.id === dealId ? { ...d, claude_pricing_log: pricingLogValue } : d))
      setEditingPricingLog(null)
    } catch (error) {
      alert(`Error saving pricing log: ${error.message}`)
    } finally {
      setSavingPricingLog(false)
    }
  }

  // Update deal status to Quote Ready (or Part Ex for PX deals)
  async function updateDealStatus(dealId, stageId = '4383256790') {
    setUpdatingDeal(dealId)
    try {
      await api.patch(`/api/deals/${dealId}/status`, { dealstage: stageId })
      setDeals(deals.filter(d => d.id !== dealId))
    } catch (error) {
      alert(`Error: ${error.message}`)
    } finally {
      setUpdatingDeal(null)
    }
  }

  // Reject deal
  async function rejectDeal(dealId) {
    if (!confirm('Are you sure you want to reject this deal? An email will be sent to the customer.')) return
    setRejectingDeal(dealId)
    try {
      const result = await api.post(`/api/deals/${dealId}/reject`)
      setDeals(deals.filter(d => d.id !== dealId))
      if (result.emailSent) alert(`Deal rejected. Email sent to ${result.email}`)
      else if (result.emailError) alert(`Deal rejected but email failed: ${result.emailError}`)
      else alert('Deal rejected. No email was sent.')
    } catch (error) {
      alert(`Error: ${error.message}`)
    } finally {
      setRejectingDeal(null)
    }
  }

  // Send more info request
  async function sendMoreInfoRequest() {
    if (!moreInfoOptions.photosInfo && !moreInfoOptions.other) {
      alert('Please select at least one option.')
      return
    }
    if (moreInfoOptions.other && !otherText.trim()) {
      alert('Please enter the additional information required.')
      return
    }

    setSendingMoreInfo(true)
    try {
      const result = await api.post(`/api/deals/${moreInfoDeal}/more-info`, {
        photosInfo: moreInfoOptions.photosInfo,
        otherText: moreInfoOptions.other ? otherText.trim() : null
      })
      setDeals(deals.filter(d => d.id !== moreInfoDeal))
      setMoreInfoDeal(null)
      setMoreInfoOptions({ photosInfo: false, other: false })
      setOtherText('')
      if (result.emailSent) alert(`More info requested. Email sent to ${result.email}`)
    } catch (error) {
      alert(`Error: ${error.message}`)
    } finally {
      setSendingMoreInfo(false)
    }
  }

  // Send consignment offer
  async function sendConsignmentOffer(dealId) {
    if (!confirm('Send consignment offer to customer?')) return
    setSendingConsignment(dealId)
    try {
      const result = await api.post(`/api/deals/${dealId}/consignment`)
      setDeals(deals.filter(d => d.id !== dealId))
      if (result.emailSent) alert(`Consignment offer sent to ${result.email}`)
    } catch (error) {
      alert(`Error: ${error.message}`)
    } finally {
      setSendingConsignment(null)
    }
  }

  // Mark as duplicate
  async function markDealAsDuplicate(dealId) {
    if (!confirm('Mark this deal as duplicate?')) return
    setMarkingDuplicate(dealId)
    try {
      await api.patch(`/api/deals/${dealId}/status`, { dealstage: '1616888039' })
      setDeals(deals.filter(d => d.id !== dealId))
    } catch (error) {
      alert(`Error: ${error.message}`)
    } finally {
      setMarkingDuplicate(null)
    }
  }

  // Submit badly priced feedback
  async function submitBadlyPriced() {
    if (!badlyPricedDeal) return
    const deal = deals.find(d => d.id === badlyPricedDeal)
    if (!deal) return

    const items = parseItems(deal.parsed_items)
    const flaggedItems = Object.entries(badlyPricedItems).map(([idx, issueType]) => {
      const parsed = parseItemLine(items[idx])
      return {
        index: parseInt(idx),
        description: parsed.description,
        sp: parsed.sp,
        bp: parsed.bp,
        score: parsed.score,
        issueType,
        comment: badlyPricedComments[idx]?.trim() || null
      }
    })

    if (flaggedItems.length === 0) return

    setSubmittingBadlyPriced(true)
    try {
      await api.post(`/api/deals/${deal.id}/pricing-feedback`, {
        items: flaggedItems,
        dealName: deal.dealname,
        parsedItems: deal.parsed_items || null,
        claudePricingLog: deal.claude_pricing_log || null
      })
      setBadlyPricedDeal(null)
      setBadlyPricedItems({})
      setBadlyPricedComments({})
      setBadlyPricedSuccess(deal.id)
      setTimeout(() => setBadlyPricedSuccess(null), 2000)
    } catch (error) {
      alert(`Error: ${error.message}`)
    } finally {
      setSubmittingBadlyPriced(false)
    }
  }

  // Update other deal stage from popup
  async function updateOtherDealStage(dealId, newStage, parentDealId) {
    setUpdatingOtherDealStage(true)
    try {
      await api.patch(`/api/deals/${dealId}/status`, { dealstage: newStage })
      // Update in customerDeals cache
      setCustomerDeals(prev => ({
        ...prev,
        [parentDealId]: prev[parentDealId]?.map(d =>
          d.id === dealId ? { ...d, dealstage: newStage, stageName: getStageName(newStage) } : d
        )
      }))
      setSelectedOtherDeal(prev => prev ? { ...prev, dealstage: newStage } : null)
    } catch (error) {
      alert(`Error: ${error.message}`)
    } finally {
      setUpdatingOtherDealStage(false)
    }
  }

  // Mark other deal as duplicate from popup
  async function markOtherDealAsDuplicate(dealId, parentDealId) {
    if (!confirm('Mark this deal as duplicate?')) return
    setMarkingOtherDuplicate(true)
    try {
      await api.patch(`/api/deals/${dealId}/status`, { dealstage: '1616888039' })
      // Remove from customerDeals cache
      setCustomerDeals(prev => ({
        ...prev,
        [parentDealId]: prev[parentDealId]?.filter(d => d.id !== dealId)
      }))
      setSelectedOtherDeal(null)
    } catch (error) {
      alert(`Error: ${error.message}`)
    } finally {
      setMarkingOtherDuplicate(false)
    }
  }

  // Toggle customer dropdown
  async function toggleExpanded(dealId, email) {
    if (expandedDeal === dealId) {
      setExpandedDeal(null)
    } else {
      setExpandedDeal(dealId)
      if (!customerDeals[dealId] && email) {
        setLoadingCustomerDeals(prev => ({ ...prev, [dealId]: true }))
        try {
          const result = await api.get(`/api/deals/customer-deals?email=${encodeURIComponent(email)}`)
          setCustomerDeals(prev => ({ ...prev, [dealId]: result.deals || [] }))
        } catch (error) {
          console.error('Error:', error)
        } finally {
          setLoadingCustomerDeals(prev => ({ ...prev, [dealId]: false }))
        }
      }
    }
  }

  // Toggle deal images
  async function toggleDealImages(dealId) {
    if (expandedDealImages === dealId) {
      setExpandedDealImages(null)
      return
    }
    setExpandedDealImages(dealId)
    if (dealImages[dealId]) return

    setLoadingDealImages(dealId)
    try {
      const result = await api.get(`/api/deals/${dealId}/images`)
      setDealImages(prev => ({ ...prev, [dealId]: result.images || [] }))
    } catch (error) {
      console.error('Error:', error)
      setDealImages(prev => ({ ...prev, [dealId]: [] }))
    } finally {
      setLoadingDealImages(null)
    }
  }

  // Filter deals by search term
  const filteredDeals = deals.filter(deal => {
    const searchLower = searchTerm.toLowerCase()
    return (
      (deal.dealname || '').toLowerCase().includes(searchLower) ||
      (deal.id || '').includes(searchLower) ||
      (deal.parsed_items || '').toLowerCase().includes(searchLower) ||
      (deal.seller_email || '').toLowerCase().includes(searchLower)
    )
  })

  async function repriceItem(dealId, itemIndex) {
    setRepricingItem({ dealId, index: itemIndex })
    try {
      const response = await api.post(`/api/deals/${dealId}/reprice-item`, { itemIndex })
      if (response.newParsedItems) {
        setDeals(deals.map(d => d.id === dealId ? {
          ...d,
          parsed_items: response.newParsedItems,
          ...(response.newPricingLog ? { claude_pricing_log: response.newPricingLog } : {})
        } : d))
      }
    } catch (error) {
      alert(`Re-price failed: ${error.message || 'Unknown error'}`)
    } finally {
      setRepricingItem(null)
    }
  }

  // Render parsed items inline
  function renderParsedItems(deal) {
    const items = parseItems(deal.parsed_items)
    const isSaving = savingField?.startsWith(`${deal.id}-parsed_items`)

    return (
      <div className="parsed-items-list">
        {items.map((item, index) => {
          const parsed = parseItemLine(item)
          const urls = getSearchUrls(item)
          const isSelected = selectedItem?.dealId === deal.id && selectedItem?.index === index
          const isEditingSP = editingPrice?.dealId === deal.id && editingPrice?.index === index && editingPrice?.field === 'sp'
          const isEditingBP = editingPrice?.dealId === deal.id && editingPrice?.index === index && editingPrice?.field === 'bp'
          const isEditingQty = editingPrice?.dealId === deal.id && editingPrice?.index === index && editingPrice?.field === 'qty'
          const isEditingDescription = editingPrice?.dealId === deal.id && editingPrice?.index === index && editingPrice?.field === 'description'
          const isEditingCondition = editingPrice?.dealId === deal.id && editingPrice?.index === index && editingPrice?.field === 'condition'
          const effectiveCondition = parsed.condition || deal.condition || null

          // Use parsed score if available, otherwise fall back to log score for single-item deals
          const score = parsed.score !== null ? parsed.score : (items.length === 1 ? getScoreFromLog(deal.claude_pricing_log) : null)

          return (
            <div key={index} className={`parsed-item-card ${isSelected ? 'selected' : ''}`}>
              <div className="parsed-item-main">
                <span className="parsed-item-number">{index + 1}</span>
                {score !== null && (
                  <span className={`score-badge ${score >= 150 ? 'high' : score >= 100 ? 'medium' : score >= 45 ? 'low' : 'very-low'}`}>
                    {score}
                  </span>
                )}
                {isEditingCondition ? (
                  <select
                    className="condition-select"
                    value={priceValue}
                    onChange={async (e) => {
                      const val = e.target.value
                      setEditingPrice(null)
                      await updateItemField(deal.id, index, 'condition', val, items)
                    }}
                    onBlur={() => setEditingPrice(null)}
                    autoFocus
                  >
                    <option value="">None</option>
                    <option value="New">New</option>
                    <option value="Mint">Mint</option>
                    <option value="Excellent">Excellent</option>
                    <option value="Good">Good</option>
                    <option value="Fair">Fair</option>
                    <option value="Poor">Poor</option>
                    <option value="Not Working">Not Working</option>
                  </select>
                ) : (
                  <span
                    className={`badge badge-sm editable ${effectiveCondition ? getConditionClass(effectiveCondition) : 'badge-neutral'}`}
                    onClick={() => {
                      setEditingPrice({ dealId: deal.id, index, field: 'condition' })
                      setPriceValue(effectiveCondition || '')
                    }}
                  >
                    {effectiveCondition || 'cond'}
                  </span>
                )}
                {isEditingDescription ? (
                  <input
                    type="text"
                    className="description-input"
                    value={priceValue}
                    onChange={e => setPriceValue(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') saveFieldEdit(deal.id, index, 'description', items)
                      if (e.key === 'Escape') setEditingPrice(null)
                    }}
                    onBlur={() => saveFieldEdit(deal.id, index, 'description', items)}
                    autoFocus
                  />
                ) : (
                  <span
                    className="parsed-item-description editable"
                    onClick={() => {
                      setEditingPrice({ dealId: deal.id, index, field: 'description' })
                      setPriceValue(parsed.description)
                    }}
                  >
                    {parsed.description}
                  </span>
                )}
              </div>
              <div className="parsed-item-prices">
                <div className="price-field">
                  <span className="price-label">Qty:</span>
                  {isEditingQty ? (
                    <input
                      type="number"
                      className="price-input"
                      value={priceValue}
                      onChange={e => setPriceValue(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') saveFieldEdit(deal.id, index, 'qty', items)
                        if (e.key === 'Escape') setEditingPrice(null)
                      }}
                      onBlur={() => saveFieldEdit(deal.id, index, 'qty', items)}
                      autoFocus
                    />
                  ) : (
                    <span
                      className="price-value editable"
                      onClick={() => {
                        setEditingPrice({ dealId: deal.id, index, field: 'qty' })
                        setPriceValue(parsed.qty)
                      }}
                    >
                      {parsed.qty}
                    </span>
                  )}
                </div>
                <div className="price-field">
                  <span className="price-label">SP:</span>
                  {isEditingSP ? (
                    <input
                      type="number"
                      className="price-input"
                      value={priceValue}
                      onChange={e => setPriceValue(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') saveFieldEdit(deal.id, index, 'sp', items)
                        if (e.key === 'Escape') setEditingPrice(null)
                      }}
                      onBlur={() => saveFieldEdit(deal.id, index, 'sp', items)}
                      autoFocus
                    />
                  ) : (
                    <span
                      className="price-value price-sp editable"
                      onClick={() => {
                        setEditingPrice({ dealId: deal.id, index, field: 'sp' })
                        setPriceValue(parsed.sp)
                      }}
                    >
                      £{parsed.sp}
                    </span>
                  )}
                </div>
                <div className="price-field">
                  <span className="price-label">BP:</span>
                  {isEditingBP ? (
                    <input
                      type="number"
                      className="price-input"
                      value={priceValue}
                      onChange={e => setPriceValue(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') saveFieldEdit(deal.id, index, 'bp', items)
                        if (e.key === 'Escape') setEditingPrice(null)
                      }}
                      onBlur={() => saveFieldEdit(deal.id, index, 'bp', items)}
                      autoFocus
                    />
                  ) : (
                    <span
                      className="price-value price-bp editable"
                      onClick={() => {
                        setEditingPrice({ dealId: deal.id, index, field: 'bp' })
                        setPriceValue(parsed.bp)
                      }}
                    >
                      £{parsed.bp}
                    </span>
                  )}
                </div>
              </div>
              <div className="parsed-item-actions">
                <button
                  className="search-btn reprice-btn"
                  onClick={(e) => { e.stopPropagation(); repriceItem(deal.id, index); }}
                  disabled={repricingItem?.dealId === deal.id && repricingItem?.index === index}
                  title="Re-price this item"
                >
                  {repricingItem?.dealId === deal.id && repricingItem?.index === index ? '...' : '£'}
                </button>
                <button onClick={(e) => { e.stopPropagation(); openWindow(urls.google); }} title="Google" className="search-btn">G</button>
                <button onClick={(e) => { e.stopPropagation(); openWindow(urls.ebaySold); }} title="eBay Sold" className="search-btn">eS</button>
                <button onClick={(e) => { e.stopPropagation(); openWindow(urls.ebayActive); }} title="eBay Active" className="search-btn">eA</button>
                <button onClick={(e) => { e.stopPropagation(); openWindow(urls.reverbSold); }} title="Reverb Sold" className="search-btn">rS</button>
                <button onClick={(e) => { e.stopPropagation(); openWindow(urls.reverbActive); }} title="Reverb Active" className="search-btn">rA</button>
                <button
                  className="search-btn delete-btn"
                  onClick={() => {
                    if (confirm(`Remove item ${index + 1}: ${parsed.description}?`)) {
                      deleteParsedItem(deal.id, index, items)
                    }
                  }}
                  title="Delete item"
                >
                  ×
                </button>
              </div>
              <button
                className="expand-btn"
                onClick={() => setSelectedItem(isSelected ? null : { dealId: deal.id, index })}
              >
                {isSelected ? '▲' : '▼'}
              </button>

              {/* Expanded item details */}
              {isSelected && (
                <div className="item-details-panel">
                  <div className="item-images-section">
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => toggleDealImages(deal.id)}
                    >
                      {expandedDealImages === deal.id ? 'Hide Images' : 'View Images'}
                    </button>
                    {deal.collection_link && (
                      <a href={deal.collection_link} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-secondary" style={{ marginLeft: 8 }}>
                        Collection Link
                      </a>
                    )}
                    {expandedDealImages === deal.id && (
                      <div className="deal-images-container">
                        {loadingDealImages === deal.id ? (
                          <div className="loading-spinner" />
                        ) : dealImages[deal.id]?.length > 0 ? (
                          <div className="jotform-images-grid">
                            {dealImages[deal.id].map((url, idx) => (
                              <img
                                key={idx}
                                src={url}
                                alt={`Image ${idx + 1}`}
                                className="jotform-image"
                                onClick={() => openLightbox(dealImages[deal.id], idx)}
                              />
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted">No images found</span>
                        )}
                      </div>
                    )}
                  </div>
                  {(deal.claude_pricing_log || editingPricingLog === deal.id) && (
                    <div className="pricing-log-section">
                      <div className="pricing-log-header">
                        <strong>Pricing Log:</strong>
                        {(() => {
                          const badge = getValidationBadge(deal.claude_pricing_log)
                          return badge ? <span className={badge.className}>{badge.label}</span> : null
                        })()}
                        <div style={{ display: 'flex', gap: '4px' }}>
                          {editingPricingLog === deal.id ? (
                            <>
                              <button
                                className="btn btn-xs btn-primary"
                                onClick={() => savePricingLog(deal.id)}
                                disabled={savingPricingLog}
                              >
                                {savingPricingLog ? 'Saving...' : 'Save'}
                              </button>
                              <button
                                className="btn btn-xs btn-secondary"
                                onClick={() => {
                                  setEditingPricingLog(null)
                                  setPricingLogValue('')
                                }}
                                disabled={savingPricingLog}
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                className="btn btn-xs btn-secondary"
                                onClick={() => {
                                  setEditingPricingLog(deal.id)
                                  setPricingLogValue(deal.claude_pricing_log || '')
                                }}
                              >
                                Edit
                              </button>
                              {deal.claude_pricing_log && (
                                <button
                                  className="btn btn-xs btn-secondary"
                                  onClick={() => {
                                    navigator.clipboard.writeText(deal.claude_pricing_log)
                                    alert('Pricing log copied to clipboard!')
                                  }}
                                >
                                  Copy
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      {editingPricingLog === deal.id ? (
                        <textarea
                          className="form-input"
                          rows={10}
                          value={pricingLogValue}
                          onChange={e => setPricingLogValue(e.target.value)}
                          style={{ fontFamily: 'monospace', fontSize: '0.8rem', width: '100%' }}
                          autoFocus
                        />
                      ) : (
                        <div className="pricing-log">
                          {parsePricingLogSections(deal.claude_pricing_log).map((sec, idx) => (
                            <div key={idx} className="pricing-log-block">
                              <div className="pricing-log-block-header">{sec.title}</div>
                              <pre className="pricing-log-block-content">{sec.content}</pre>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
        <button
          className="add-item-btn"
          onClick={() => addParsedItem(deal.id, items)}
          disabled={isSaving}
        >
          + Add Item
        </button>
      </div>
    )
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
        <div className="loading-spinner" />
      </div>
    )
  }

  return (
    <>
      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{deals.length}</div>
          <div className="stat-label">Total Deals</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: 'var(--color-success)' }}>{deals.filter(d => d.claude_pricing_log).length}</div>
          <div className="stat-label">With Pricing Log</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: 'var(--color-warning)' }}>{deals.filter(d => !d.claude_pricing_log).length}</div>
          <div className="stat-label">Awaiting Pricing</div>
        </div>
      </div>

      {/* Search */}
      <div className="card" style={{ marginBottom: 'var(--space-4)', padding: 'var(--space-3)' }}>
        <input
          type="text"
          className="form-input"
          placeholder="Search deals by name, items, or email..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Deals Table */}
      <div className="card">
        <table className="table deals-table">
          <thead>
            <tr>
              <th>Deal Name</th>
              <th>Parsed Items</th>
              <th>Customer</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredDeals.length === 0 ? (
              <tr>
                <td colSpan="4" style={{ textAlign: 'center', padding: 'var(--space-8)' }}>
                  {searchTerm ? 'No deals match your search' : 'No deals in Quote Requested stage'}
                </td>
              </tr>
            ) : (
              filteredDeals.map(deal => (
                <tr key={deal.id}>
                  <td className="title-cell">
                    <div className="deal-name">{deal.dealname || 'Unnamed Deal'} ({deal.id})</div>
                  </td>
                  <td className="parsed-items-cell">
                    {renderParsedItems(deal)}
                  </td>
                  <td className="customer-cell">
                    {deal.seller_email ? (
                      <div className="customer-info">
                        <button
                          className={`customer-email-btn ${expandedDeal === deal.id ? 'active' : ''}`}
                          onClick={() => toggleExpanded(deal.id, deal.seller_email)}
                        >
                          {deal.seller_email}
                          <span className="expand-arrow">{expandedDeal === deal.id ? '▲' : '▼'}</span>
                        </button>
                        {expandedDeal === deal.id && (
                          <div className="customer-deals-dropdown">
                            <div className="dropdown-section-header">DEALS</div>
                            {loadingCustomerDeals[deal.id] ? (
                              <div style={{ padding: 16, textAlign: 'center' }}><div className="loading-spinner" /></div>
                            ) : customerDeals[deal.id]?.length > 0 ? (
                              <div className="customer-deals-list">
                                {customerDeals[deal.id].map(cd => {
                                  const isCurrentDeal = cd.id === deal.id
                                  const stageClass = getStageClass(cd.dealstage)
                                  return (
                                    <div
                                      key={cd.id}
                                      className={`other-deal-item ${isCurrentDeal ? 'current' : 'clickable'}`}
                                      onClick={() => !isCurrentDeal && setSelectedOtherDeal({ ...cd, parentDealId: deal.id })}
                                    >
                                      <div className="other-deal-header">
                                        <span className={`badge badge-sm ${stageClass}`}>{getStageName(cd.dealstage)}</span>
                                        <span className="other-deal-date">{formatDate(cd.createdate)}</span>
                                        <span className="other-deal-amount">{formatCurrency(cd.amount)}</span>
                                      </div>
                                      <div className="other-deal-items">
                                        {cd.parsed_items?.split('\n').slice(0, 2).map((item, i) => (
                                          <div key={i} className="other-deal-item-line">{item}</div>
                                        ))}
                                        {cd.parsed_items?.split('\n').length > 2 && (
                                          <div className="other-deal-item-more">+{cd.parsed_items.split('\n').length - 2} more</div>
                                        )}
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                            ) : (
                              <div className="text-muted" style={{ padding: 12 }}>No deals found</div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted">No email</span>
                    )}
                  </td>
                  <td className="actions-cell">
                    <div className="action-buttons-grid">
                      <div className="action-row-1">
                        {deal.is_part_exchange ? (
                          <button
                            className="btn btn-sm btn-quote-ready"
                            onClick={() => updateDealStatus(deal.id, '4962263243')}
                            disabled={updatingDeal === deal.id}
                            style={{ background: '#7c3aed', borderColor: '#7c3aed' }}
                          >
                            {updatingDeal === deal.id ? '...' : 'Part-Ex Ready'}
                          </button>
                        ) : (
                          <button
                            className="btn btn-sm btn-quote-ready"
                            onClick={() => updateDealStatus(deal.id)}
                            disabled={updatingDeal === deal.id}
                          >
                            {updatingDeal === deal.id ? '...' : 'Quote Ready'}
                          </button>
                        )}
                        <button
                          className="btn btn-sm btn-reject"
                          onClick={() => rejectDeal(deal.id)}
                          disabled={rejectingDeal === deal.id}
                        >
                          {rejectingDeal === deal.id ? '...' : 'Reject'}
                        </button>
                      </div>
                      <div className="action-row-2">
                        <button
                          className="btn btn-sm btn-moreinfo"
                          onClick={() => setMoreInfoDeal(deal.id)}
                        >
                          More Info
                        </button>
                        {!deal.is_part_exchange && (
                          <button
                            className="btn btn-sm btn-consignment"
                            onClick={() => sendConsignmentOffer(deal.id)}
                            disabled={sendingConsignment === deal.id}
                          >
                            {sendingConsignment === deal.id ? '...' : 'Consignment'}
                          </button>
                        )}
                        <button
                          className="btn btn-sm btn-duplicate"
                          onClick={() => markDealAsDuplicate(deal.id)}
                          disabled={markingDuplicate === deal.id}
                        >
                          {markingDuplicate === deal.id ? '...' : 'Duplicate'}
                        </button>
                      </div>
                      <div className="action-row-3">
                        <button
                          className={`btn btn-sm btn-badly-priced${badlyPricedSuccess === deal.id ? ' btn-reported' : ''}`}
                          onClick={() => { setBadlyPricedDeal(deal.id); setBadlyPricedItems({}); setBadlyPricedComments({}) }}
                          disabled={submittingBadlyPriced}
                        >
                          {badlyPricedSuccess === deal.id ? 'Reported' : 'Badly Priced'}
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* More Info Modal */}
      {moreInfoDeal && (
        <div className="modal-overlay" onClick={() => setMoreInfoDeal(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Request More Information</h2>
              <button className="modal-close" onClick={() => setMoreInfoDeal(null)}>&times;</button>
            </div>
            <div className="modal-body-scroll">
              <p>Select what information is needed:</p>
              <div style={{ marginBottom: 'var(--space-3)' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={moreInfoOptions.photosInfo}
                    onChange={() => setMoreInfoOptions(p => ({ ...p, photosInfo: !p.photosInfo }))}
                  />
                  Photos & detailed info (brand, model)
                </label>
              </div>
              <div style={{ marginBottom: 'var(--space-3)' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={moreInfoOptions.other}
                    onChange={() => setMoreInfoOptions(p => ({ ...p, other: !p.other }))}
                  />
                  Other (specify below)
                </label>
              </div>
              {moreInfoOptions.other && (
                <textarea
                  className="form-input"
                  rows={3}
                  placeholder="Enter the additional information required..."
                  value={otherText}
                  onChange={e => setOtherText(e.target.value)}
                />
              )}
            </div>
            <div className="modal-footer" style={{ padding: 'var(--space-4)' }}>
              <button className="btn btn-secondary" onClick={() => setMoreInfoDeal(null)}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={sendMoreInfoRequest}
                disabled={sendingMoreInfo}
              >
                {sendingMoreInfo ? 'Sending...' : 'Send Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Badly Priced Modal */}
      {badlyPricedDeal && (() => {
        const deal = deals.find(d => d.id === badlyPricedDeal)
        if (!deal) return null
        const items = parseItems(deal.parsed_items)
        const selectedCount = Object.keys(badlyPricedItems).length
        return (
          <div className="modal-overlay" onClick={() => setBadlyPricedDeal(null)}>
            <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
              <div className="modal-header">
                <h2>Report Badly Priced Items</h2>
                <button className="modal-close" onClick={() => setBadlyPricedDeal(null)}>&times;</button>
              </div>
              <div className="modal-body-scroll">
                <p style={{ marginBottom: 12, color: 'var(--color-text-secondary)' }}>
                  {deal.dealname} — select items with pricing issues:
                </p>
                {items.length === 0 ? (
                  <p>No parsed items found for this deal.</p>
                ) : (
                  items.map((line, idx) => {
                    const parsed = parseItemLine(line)
                    const isChecked = idx in badlyPricedItems
                    return (
                      <div key={idx} className={`badly-priced-item${isChecked ? ' badly-priced-item--checked' : ''}`}>
                        <div className="badly-priced-item-header">
                          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', flex: 1 }}>
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => {
                                if (isChecked) {
                                  const next = { ...badlyPricedItems }
                                  delete next[idx]
                                  setBadlyPricedItems(next)
                                } else {
                                  setBadlyPricedItems({ ...badlyPricedItems, [idx]: 'sp_too_high' })
                                }
                              }}
                            />
                            <span style={{ fontWeight: 500 }}>{idx + 1}. {parsed.description}</span>
                          </label>
                          <span style={{ fontSize: 12, color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
                            SP:£{parsed.sp} BP:£{parsed.bp}{parsed.score !== null ? ` Sc:${parsed.score}` : ''}
                          </span>
                        </div>
                        {isChecked && (
                          <div className="badly-priced-issue-type">
                            {[
                              { value: 'sp_too_high', label: 'SP too high' },
                              { value: 'sp_too_low', label: 'SP too low' },
                              { value: 'low_score_many_refs', label: 'Low points when lots of references found' },
                              { value: 'wrong_product', label: 'Wrong product detected' }
                            ].map(opt => (
                              <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                                <input
                                  type="radio"
                                  name={`issue-${idx}`}
                                  checked={badlyPricedItems[idx] === opt.value}
                                  onChange={() => setBadlyPricedItems({ ...badlyPricedItems, [idx]: opt.value })}
                                />
                                {opt.label}
                              </label>
                            ))}
                            <textarea
                              className="form-input"
                              rows={2}
                              placeholder="Add a comment (optional) — e.g. why the SP is too high..."
                              value={badlyPricedComments[idx] || ''}
                              onChange={e => setBadlyPricedComments({ ...badlyPricedComments, [idx]: e.target.value })}
                              style={{ marginTop: 8, fontSize: '0.85rem' }}
                            />
                          </div>
                        )}
                      </div>
                    )
                  })
                )}
              </div>
              <div className="modal-footer" style={{ padding: 'var(--space-4)' }}>
                <button className="btn btn-secondary" onClick={() => setBadlyPricedDeal(null)}>Cancel</button>
                <button
                  className="btn btn-badly-priced"
                  onClick={submitBadlyPriced}
                  disabled={selectedCount === 0 || submittingBadlyPriced}
                >
                  {submittingBadlyPriced ? 'Submitting...' : `Report ${selectedCount} Item${selectedCount !== 1 ? 's' : ''}`}
                </button>
              </div>
            </div>
          </div>
        )
      })()}

      {/* Lightbox */}
      {lightbox.open && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={e => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}>&times;</button>

            {lightbox.images.length > 1 && (
              <button className="lightbox-nav lightbox-prev" onClick={prevImage}>&#8249;</button>
            )}

            <img
              src={lightbox.images[lightbox.currentIndex]}
              alt={`Image ${lightbox.currentIndex + 1}`}
              className="lightbox-image"
            />

            {lightbox.images.length > 1 && (
              <button className="lightbox-nav lightbox-next" onClick={nextImage}>&#8250;</button>
            )}

            <div className="lightbox-counter">
              {lightbox.currentIndex + 1} / {lightbox.images.length}
            </div>

            {lightbox.images.length > 1 && (
              <div className="lightbox-thumbnails">
                {lightbox.images.map((url, idx) => (
                  <img
                    key={idx}
                    src={url}
                    alt={`Thumbnail ${idx + 1}`}
                    className={`lightbox-thumb ${idx === lightbox.currentIndex ? 'active' : ''}`}
                    onClick={() => setLightbox(prev => ({ ...prev, currentIndex: idx }))}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Other Deal Details Popup */}
      {selectedOtherDeal && (
        <div className="modal-overlay" onClick={() => setSelectedOtherDeal(null)}>
          <div className="modal-content modal-deal-detail" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Deal Details</h2>
              <button className="modal-close" onClick={() => setSelectedOtherDeal(null)}>&times;</button>
            </div>
            <div className="modal-body-scroll">
              {/* Deal name */}
              <div className="popup-dealname">{selectedOtherDeal.dealname || 'Unnamed Deal'}</div>
              <div className="popup-deal-id">ID: {selectedOtherDeal.id}</div>

              {/* Date at top */}
              <div className="popup-date-row">
                <span className="popup-date">{formatDate(selectedOtherDeal.createdate)}</span>
                <span className="popup-amount">{formatCurrency(selectedOtherDeal.amount)}</span>
              </div>

              {/* Stage with dropdown */}
              <div className="popup-stage-row">
                <span className={`badge ${getStageClass(selectedOtherDeal.dealstage)}`}>
                  {getStageName(selectedOtherDeal.dealstage)}
                </span>
                <select
                  className="form-input form-input-sm"
                  value={selectedOtherDeal.dealstage || ''}
                  onChange={e => updateOtherDealStage(selectedOtherDeal.id, e.target.value, selectedOtherDeal.parentDealId)}
                  disabled={updatingOtherDealStage}
                  style={{ flex: 1, marginLeft: 12 }}
                >
                  {DEAL_STAGES.filter(stage => stage.id).map(stage => (
                    <option key={stage.id} value={stage.id}>{stage.label}</option>
                  ))}
                </select>
              </div>

              {/* Parsed items - each showing name and BP */}
              <div className="popup-items-section">
                <div className="deal-detail-label">ITEMS</div>
                {selectedOtherDeal.parsed_items && selectedOtherDeal.parsed_items.trim() ? (
                  <div className="popup-items-list">
                    {selectedOtherDeal.parsed_items.split('\n').filter(line => line.trim()).map((item, i) => {
                      const parsed = parseItemLine(item)
                      return (
                        <div key={i} className="popup-item-row">
                          <span className="popup-item-num">{i + 1}.</span>
                          <span className="popup-item-name">{parsed.description || item}</span>
                          <span className="popup-item-bp">BP: £{parsed.bp || '0'}</span>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="text-muted" style={{ padding: '8px 0' }}>No parsed items - Deal name: {selectedOtherDeal.dealname}</div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-reject"
                onClick={() => markOtherDealAsDuplicate(selectedOtherDeal.id, selectedOtherDeal.parentDealId)}
                disabled={markingOtherDuplicate}
              >
                {markingOtherDuplicate ? 'Marking...' : 'Mark as Duplicate'}
              </button>
              <button className="btn btn-secondary" onClick={() => setSelectedOtherDeal(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// Get condition badge class
function getConditionClass(condition) {
  const c = (condition || '').toLowerCase()
  if (c === 'excellent') return 'condition-excellent'
  if (c === 'new' || c === 'mint') return 'condition-new'
  if (c === 'good') return 'condition-good'
  if (c === 'fair') return 'condition-fair'
  if (c === 'poor') return 'condition-poor'
  if (c === 'not working') return 'condition-not-working'
  return 'badge-neutral'
}

// Get stage badge class based on stage name
function getStageClass(stageId) {
  // Quote stages - teal/green
  if (['4383256790', '24798453', '65923846', 'appointmentscheduled'].includes(stageId)) {
    return 'stage-quote-sent'
  }
  // Quote Requested - orange/yellow
  if (['112025330', '24798454'].includes(stageId)) {
    return 'stage-quote-requested'
  }
  // Completed stages - green
  if (['closedwon', '126498898', '24798455'].includes(stageId)) {
    return 'stage-completed'
  }
  // Lost/rejected stages - red
  if (['closedlost', '24798456', '1616888039'].includes(stageId)) {
    return 'stage-lost'
  }
  return 'badge-neutral'
}

export default QuoteRequested
